import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Button, Card, Field, formatDate, formatTime } from '../../components/ui';
import { useToast } from '../../components/Toast';

const today = new Date().toISOString().slice(0, 10);
const oneYearOut = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 365);
  return d.toISOString().slice(0, 10);
})();

const WEEKDAY_VALUES = [
  { value: 1, key: 'mon' },
  { value: 2, key: 'tue' },
  { value: 3, key: 'wed' },
  { value: 4, key: 'thu' },
  { value: 5, key: 'fri' },
  { value: 6, key: 'sat' },
  { value: 0, key: 'sun' },
];

function countWeeklyOccurrences(startDate, endDate, weekdays) {
  if (!startDate || !endDate || !weekdays.length || endDate < startDate) return 0;
  const days = new Set(weekdays);
  let count = 0;
  const cursor = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  while (cursor <= end) {
    if (days.has(cursor.getUTCDay())) count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}

export default function FamilyRequest() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState('single');
  const [form, setForm] = useState({
    bookingDate: '', startDate: '', endDate: '', weekdays: [], dates: [],
    startTime: '17:00', endTime: '22:00',
    childrenCount: 1, requirement: t('familyRequest.defaultRequirement'), notes: '',
  });
  const [newDate, setNewDate] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const MODES = [
    { value: 'single', title: t('familyRequest.modeSingleTitle'), hint: t('familyRequest.modeSingleHint') },
    { value: 'weekly', title: t('familyRequest.modeWeeklyTitle'), hint: t('familyRequest.modeWeeklyHint') },
    { value: 'custom', title: t('familyRequest.modeCustomTitle'), hint: t('familyRequest.modeCustomHint') },
  ];

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  function toggleWeekday(value) {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(value) ? f.weekdays.filter((v) => v !== value) : [...f.weekdays, value],
    }));
  }

  function addDate() {
    if (!newDate) return;
    if (form.dates.includes(newDate)) {
      setNewDate('');
      return;
    }
    setForm((f) => ({ ...f, dates: [...f.dates, newDate].sort() }));
    setNewDate('');
  }

  function removeDate(date) {
    setForm((f) => ({ ...f, dates: f.dates.filter((d) => d !== date) }));
  }

  const occurrenceCount = useMemo(() => {
    if (mode === 'weekly') return countWeeklyOccurrences(form.startDate, form.endDate, form.weekdays);
    if (mode === 'custom') return form.dates.length;
    return form.bookingDate ? 1 : 0;
  }, [mode, form]);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});
    try {
      if (mode === 'single') {
        await api.post('/api/family/bookings', {
          bookingDate: form.bookingDate,
          startTime: form.startTime,
          endTime: form.endTime,
          childrenCount: form.childrenCount,
          requirement: form.requirement,
          notes: form.notes,
        });
      } else {
        await api.post('/api/family/bookings/series', {
          mode,
          startDate: mode === 'weekly' ? form.startDate : undefined,
          endDate: mode === 'weekly' ? form.endDate : undefined,
          weekdays: mode === 'weekly' ? form.weekdays : undefined,
          dates: mode === 'custom' ? form.dates : undefined,
          startTime: form.startTime,
          endTime: form.endTime,
          childrenCount: form.childrenCount,
          requirement: form.requirement,
          notes: form.notes,
        });
      }
      navigate('/family/bookings', { state: { flash: t('familyRequest.sentSuccess') } });
    } catch (err) {
      toast.error(err.message);
      setFieldErrors(err.fieldErrors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('familyRequest.pageTitle')}</h1>
          <p className="muted">{t('familyRequest.subtitle')}</p>
        </div>
      </div>


      <form onSubmit={submit}>
        <Card title={t('familyRequest.howOften')}>
          <div className="mode-tabs">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                className={`mode-tab ${mode === m.value ? 'is-active' : ''}`}
                onClick={() => setMode(m.value)}
              >
                <strong>{m.title}</strong>
                <span>{m.hint}</span>
              </button>
            ))}
          </div>

          {mode === 'single' && (
            <div className="grid-3">
              <Field label={t('familyRequest.date')} required error={fieldErrors.bookingDate}>
                <input type="date" min={today} value={form.bookingDate} onChange={set('bookingDate')} required />
              </Field>
              <Field label={t('familyRequest.startTime')} required error={fieldErrors.startTime}>
                <input type="time" value={form.startTime} onChange={set('startTime')} required />
              </Field>
              <Field label={t('familyRequest.endTime')} required error={fieldErrors.endTime}>
                <input type="time" value={form.endTime} onChange={set('endTime')} required />
              </Field>
            </div>
          )}

          {mode === 'weekly' && (
            <>
              <div className="grid-3">
                <Field label={t('familyRequest.startDate')} required error={fieldErrors.startDate}>
                  <input type="date" min={today} value={form.startDate} onChange={set('startDate')} required />
                </Field>
                <Field label={t('familyRequest.endDate')} required error={fieldErrors.endDate} hint={t('familyRequest.endDateHint')}>
                  <input
                    type="date"
                    min={form.startDate || today}
                    max={oneYearOut}
                    value={form.endDate}
                    onChange={set('endDate')}
                    required
                  />
                </Field>
                <div />
              </div>
              <Field label={t('familyRequest.whichDays')} required error={fieldErrors.weekdays}>
                <div className="weekday-picker">
                  {WEEKDAY_VALUES.map((d) => (
                    <button
                      key={d.value}
                      type="button"
                      className={`weekday-chip ${form.weekdays.includes(d.value) ? 'is-active' : ''}`}
                      onClick={() => toggleWeekday(d.value)}
                    >
                      {t(`familyRequest.weekdays.${d.key}`)}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid-2">
                <Field label={t('familyRequest.startTime')} required error={fieldErrors.startTime}>
                  <input type="time" value={form.startTime} onChange={set('startTime')} required />
                </Field>
                <Field label={t('familyRequest.endTime')} required error={fieldErrors.endTime}>
                  <input type="time" value={form.endTime} onChange={set('endTime')} required />
                </Field>
              </div>
            </>
          )}

          {mode === 'custom' && (
            <>
              <Field label={t('familyRequest.addDate')} error={fieldErrors.dates}>
                <div className="grid-2" style={{ alignItems: 'end' }}>
                  <input type="date" min={today} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                  <Button type="button" variant="secondary" onClick={addDate}>{t('familyRequest.addDateButton')}</Button>
                </div>
              </Field>
              {form.dates.length > 0 && (
                <ul className="date-list">
                  {form.dates.map((date) => (
                    <li key={date}>
                      <span>{formatDate(date, { long: true })}</span>
                      <Button type="button" variant="ghost" onClick={() => removeDate(date)}>{t('familyRequest.remove')}</Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid-2">
                <Field label={t('familyRequest.startTime')} required error={fieldErrors.startTime}>
                  <input type="time" value={form.startTime} onChange={set('startTime')} required />
                </Field>
                <Field label={t('familyRequest.endTime')} required error={fieldErrors.endTime}>
                  <input type="time" value={form.endTime} onChange={set('endTime')} required />
                </Field>
              </div>
            </>
          )}

          {occurrenceCount > 0 && (
            <p className="preview-line">
              {mode === 'single'
                ? t('familyRequest.singlePreview', { date: formatDate(form.bookingDate, { long: true }), start: formatTime(form.startTime), end: formatTime(form.endTime) })
                : t('familyRequest.multiPreview', { count: occurrenceCount, plural: occurrenceCount === 1 ? '' : 's', start: formatTime(form.startTime), end: formatTime(form.endTime) })}
            </p>
          )}
        </Card>

        <Card title={t('familyRequest.whatDoYouNeed')}>
          <div className="grid-2">
            <Field label={t('familyRequest.numberOfChildren')} required error={fieldErrors.childrenCount}>
              <input
                type="number"
                min="1"
                max="12"
                value={form.childrenCount}
                onChange={set('childrenCount')}
                required
              />
            </Field>
            <Field label={t('familyRequest.requirement')} hint={t('familyRequest.requirementHint')}>
              <input value={form.requirement} onChange={set('requirement')} />
            </Field>
          </div>
          <Field
            label={t('familyRequest.additionalInfo')}
            hint={t('familyRequest.additionalInfoHint')}
          >
            <textarea rows={5} value={form.notes} onChange={set('notes')} />
          </Field>
        </Card>

        <div className="form-footer">
          <Button type="submit" loading={busy}>
            {mode === 'single' ? t('familyRequest.sendRequest') : t('familyRequest.sendRequestCount', { count: occurrenceCount || 0, plural: occurrenceCount === 1 ? '' : 's' })}
          </Button>
        </div>
      </form>
    </>
  );
}
